import React, { useEffect, useMemo, useState } from 'react';

import { useI18n } from '../../i18n';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { config } from '../../config/environment';
import { authHeader } from '../../services/authToken';
import { authedApiFetch } from '../../services/tokenRefresh';
import ScorecardNavigation from '../restaurant/ScorecardNavigation';
import {
  AudienceSpec,
  ComposerRequest,
  previewAudience,
  sendAdminNotification,
} from '../../services/notificationsService';
import { NotificationCard } from './NotificationCard';
import { AppNotification } from '../../services/notificationsService';

// Cognito-aware fetch — delegates to the canonical authHeader() (feat-026).
// The composer talks to the backend directly rather than through
// managementService.*.
function authHeaders(): Record<string, string> {
  return authHeader();
}

function backendUrl(path: string): string {
  const protocol = config.environment === 'fake' || config.environment === 'local' ? 'http' : 'https';
  return `${protocol}://${config.restaurantBackendHost}${path}`;
}

async function fetchTenantLocations(tenantId: string): Promise<{ id: string; name: string }[]> {
  const url = backendUrl(`/api/tenant/${encodeURIComponent(tenantId)}/locations`);
  const resp = await authedApiFetch(url, { headers: { ...authHeaders(), 'Content-Type': 'application/json' } });
  if (!resp.ok) throw new Error(`locations ${resp.status}`);
  const data = await resp.json();
  return (Array.isArray(data) ? data : []).map((l: any) => ({ id: l.id, name: l.name }));
}

async function fetchLocationServers(tenantId: string, locationId: string): Promise<{ id: string; first_name: string; last_name: string; location_id: string }[]> {
  const url = backendUrl(`/api/tenant/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/servers`);
  const resp = await authedApiFetch(url, { headers: { ...authHeaders(), 'Content-Type': 'application/json' } });
  if (!resp.ok) throw new Error(`servers ${resp.status}`);
  const data = await resp.json();
  return Array.isArray(data) ? data : [];
}

type Variant = 'celebration' | 'manager' | 'coaching' | 'alert' | 'system' | 'shift' | 'progress';
type AudienceMode = 'all_servers_at_location' | 'all_managers_at_location' | 'pick_servers';

/**
 * Admin composer page at /restaurant/administration/notifications.
 *
 * Single-language compose for v1 (admin types in one language, every recipient
 * sees that text). Bilingual compose is a v1.1 follow-up — see NOTIFICATIONS_PLAN.md §12.7.4.
 */
export const AdminNotificationsComposerPage: React.FC = () => {
  const { user } = useAuth();
  const { selectedTenant, tenantName } = useTenant();
  const { t } = useI18n();

  const [audienceMode, setAudienceMode] = useState<AudienceMode>('all_servers_at_location');
  const [pickedServerIds, setPickedServerIds] = useState<string[]>([]);
  const [serverOptions, setServerOptions] = useState<{ id: string; name: string; locationId?: string; locationName?: string }[]>([]);
  const [locationOptions, setLocationOptions] = useState<{ id: string; name: string }[]>([]);
  const [variant, setVariant] = useState<Variant>('system');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [deepLink, setDeepLink] = useState('');
  const [scheduleNow, setScheduleNow] = useState(true);
  const [fireAt, setFireAt] = useState('');
  const [sending, setSending] = useState(false);
  const [audiencePreview, setAudiencePreview] = useState<{ total: number; by_locale: Record<string, number> } | null>(null);
  const [resultMsg, setResultMsg] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);
  const [loadingServers, setLoadingServers] = useState(false);
  const [recipientsError, setRecipientsError] = useState<string | null>(null);

  // Admins have no location_id but act tenant-wide via the TenantContext selection.
  // Managers/store managers have a fixed location_id baked into their user.
  // For admins specifically, do NOT fall back to user.managementUser.tenant_id
  // (Bespoke staff tenant); require an explicit Ladle & Leaf / customer selection.
  const userLocationId = user?.managementUser?.location_id;
  const userRoleRaw = user?.managementUser?.role ?? '';
  const isAdminRole = userRoleRaw.toLowerCase() === 'admin';
  const tenantId = isAdminRole
    ? selectedTenant /* may be null until the user picks one */
    : (selectedTenant || user?.managementUser?.tenant_id);

  // Fetch locations for the selected tenant (admins need this; managers can ignore).
  // Then fetch servers across all those locations. Direct fetch (Cognito-aware)
  // because managementService.* uses the legacy `token` key.
  useEffect(() => {
    // Always reset state when tenantId changes so stale data from a previous
    // tenant doesn't bleed through.
    setServerOptions([]);
    setLocationOptions([]);
    setRecipientsError(null);
    if (!tenantId) return;

    let cancelled = false;
    setLoadingServers(true);
    // For admins, always do tenant-wide fan-out — ignore any stale location_id
    // on the admin's user record. For managers, scope to their own location.
    const useLocationScope = !isAdminRole && !!userLocationId;
    (async () => {
      try {
        const allServers: { id: string; name: string; locationId?: string; locationName?: string }[] = [];

        if (useLocationScope) {
          const servers = await fetchLocationServers(tenantId, userLocationId!);
          allServers.push(
            ...servers.map((s) => ({
              id: s.id,
              name: `${s.first_name} ${s.last_name}`,
              locationId: s.location_id,
            }))
          );
        } else {
          const locations = await fetchTenantLocations(tenantId);
          if (cancelled) return;
          setLocationOptions(locations);
          const nameByLocId: Record<string, string> = {};
          for (const l of locations) nameByLocId[l.id] = l.name;
          const perLocation = await Promise.all(
            locations.map((l) =>
              fetchLocationServers(tenantId, l.id)
                .then((servers) => ({ locId: l.id, servers }))
                .catch((e) => { console.warn('server fetch failed', l.id, e); return { locId: l.id, servers: [] }; })
            )
          );
          for (const { locId, servers } of perLocation) {
            for (const s of servers) {
              const sid = s.location_id ?? locId;
              allServers.push({
                id: s.id,
                name: `${s.first_name} ${s.last_name}`,
                locationId: sid,
                locationName: nameByLocId[sid],
              });
            }
          }
        }
        if (!cancelled) setServerOptions(allServers);
      } catch (e: unknown) {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : String(e);
          console.error('Failed to load servers/locations', e);
          setRecipientsError(msg);
          // Already cleared above; keep them empty rather than show stale data.
          setServerOptions([]);
          setLocationOptions([]);
        }
      } finally {
        if (!cancelled) setLoadingServers(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId, userLocationId, isAdminRole]);

  // Build current AudienceSpec
  const audience: AudienceSpec | null = useMemo(() => {
    if (audienceMode === 'all_servers_at_location') {
      // For admins (no fixed location), "all servers at this location" effectively means
      // "all servers in the tenant" — explicit-ID delivery via the fetched list.
      if (serverOptions.length === 0) return null;
      return {
        kind: 'explicit',
        recipients: serverOptions.map((s) => ({ type: 'server', id: s.id })),
      } as any;
    }
    if (audienceMode === 'all_managers_at_location') {
      if (!isAdminRole && userLocationId) {
        return { kind: 'managers_at_location', location_id: userLocationId } as any;
      }
      // Admin: fan-out across all locations in the tenant via 'multiple'.
      if (locationOptions.length === 0) return null;
      return {
        kind: 'multiple',
        specs: locationOptions.map((l) => ({ kind: 'managers_at_location', location_id: l.id })),
      } as any;
    }
    if (audienceMode === 'pick_servers') {
      if (pickedServerIds.length === 0) return null;
      return {
        kind: 'explicit',
        recipients: pickedServerIds.map((id) => ({ type: 'server', id })),
      } as any;
    }
    return null;
  }, [audienceMode, userLocationId, pickedServerIds, serverOptions, locationOptions, isAdminRole]);

  // Live audience preview
  useEffect(() => {
    if (!audience) {
      setAudiencePreview(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const preview = await previewAudience(audience, tenantId ?? undefined);
        if (!cancelled) setAudiencePreview(preview);
      } catch (e) {
        if (!cancelled) setAudiencePreview(null);
      }
    })();
    return () => { cancelled = true; };
  }, [audience]);

  const isValid = audience !== null && title.trim().length > 0 && body.trim().length > 0;

  const handleSend = async () => {
    if (!isValid || !audience) return;
    setSending(true);
    setResultMsg(null);
    try {
      const req: ComposerRequest = {
        audience,
        variant,
        title: title.trim(),
        body: body.trim(),
        deep_link: deepLink.trim() || undefined,
        fire_at: scheduleNow ? undefined : (fireAt ? new Date(fireAt).toISOString() : undefined),
        tenant_id: tenantId ?? undefined,
      };
      const resp = await sendAdminNotification(req);
      setResultMsg({
        kind: 'ok',
        msg: resp.scheduled ? t('composer.scheduledSuccess') : t('composer.sentSuccess'),
      });
      // Clear form on success
      setTitle('');
      setBody('');
      setDeepLink('');
      setFireAt('');
    } catch (e: unknown) {
      setResultMsg({ kind: 'err', msg: `${t('composer.sentError')} ${e instanceof Error ? e.message : ''}` });
    } finally {
      setSending(false);
    }
  };

  const handleSendTestToMe = async () => {
    if (!user?.managementUser?.id) return;
    const req: ComposerRequest = {
      audience: { kind: 'explicit', recipients: [{ type: 'management', id: user.managementUser.id }] } as any,
      variant,
      title: title.trim() || '(test) untitled',
      body: body.trim() || '(test) empty body',
      tenant_id: tenantId ?? undefined,
    };
    setSending(true);
    try {
      await sendAdminNotification(req);
      setResultMsg({ kind: 'ok', msg: t('composer.sentSuccess') });
    } catch (e: unknown) {
      setResultMsg({ kind: 'err', msg: `${t('composer.sentError')} ${e instanceof Error ? e.message : ''}` });
    } finally {
      setSending(false);
    }
  };

  // Mock notification for the live preview pane
  const previewNotification: AppNotification = useMemo(() => ({
    id: 'preview',
    type: 'admin_message',
    category: 'system',
    title: title.trim() || 'Title preview',
    body: body.trim() || 'Body preview',
    payload: { variant },
    deep_link: deepLink.trim() || null,
    priority: 2,
    created: new Date().toISOString(),
    read_at: null,
    expires_at: null,
  }), [title, body, deepLink, variant]);

  return (
    <div className="min-h-screen bg-cream">
      <ScorecardNavigation />

      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '24px 20px' }}>
      <h1 style={{ fontSize: 28, fontWeight: 800, marginBottom: 6 }}>{t('composer.title')}</h1>
      <p style={{ color: '#6B7280', marginBottom: 16, fontSize: 14 }}>{t('composer.languageHint')}</p>

      {/* Active tenant banner */}
      {tenantId ? (
        <div style={{
          background: '#EFF6FF', border: '1px solid #BFDBFE', color: '#1E40AF',
          padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 20,
        }}>
          Sending to <strong>{tenantName}</strong>.
          {isAdminRole && <> Change the tenant from the tenant selector to retarget.</>}
          {recipientsError && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#991B1B', background: '#FEF2F2', padding: '6px 8px', borderRadius: 6 }}>
              Couldn't load recipients: {recipientsError}
            </div>
          )}
        </div>
      ) : (
        <div style={{
          background: '#FEF3C7', border: '1px solid #FDE68A', color: '#92400E',
          padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 20,
        }}>
          No tenant selected. Pick a tenant from the tenant selector to load recipients.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 24, alignItems: 'start' }}>
        {/* Left column — compose */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <Field label={t('composer.recipients.label')}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <RadioOption checked={audienceMode === 'all_servers_at_location'} onChange={() => setAudienceMode('all_servers_at_location')} label={t('composer.recipients.allServersAtLocation')} />
              <RadioOption checked={audienceMode === 'all_managers_at_location'} onChange={() => setAudienceMode('all_managers_at_location')} label={t('composer.recipients.allManagersAtLocation')} />
              <RadioOption checked={audienceMode === 'pick_servers'} onChange={() => setAudienceMode('pick_servers')} label={t('composer.recipients.pickServers')} />
              {audienceMode === 'pick_servers' && (
                <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: 8, padding: 8, marginTop: 4 }}>
                  {loadingServers && (
                    <div style={{ padding: 8, fontSize: 13, color: '#6B7280' }}>Loading servers…</div>
                  )}
                  {!loadingServers && serverOptions.length === 0 && (
                    <div style={{ padding: 8, fontSize: 13, color: '#6B7280' }}>
                      No servers found in this tenant.
                    </div>
                  )}
                  {[...serverOptions]
                    .sort((a, b) =>
                      (a.locationName ?? '').localeCompare(b.locationName ?? '') ||
                      a.name.localeCompare(b.name)
                    )
                    .map((s) => (
                      <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 4, fontSize: 13 }}>
                        <input
                          type="checkbox"
                          checked={pickedServerIds.includes(s.id)}
                          onChange={(e) => {
                            setPickedServerIds((prev) =>
                              e.target.checked ? [...prev, s.id] : prev.filter((x) => x !== s.id)
                            );
                          }}
                        />
                        <span>{s.name}</span>
                        {s.locationName && (
                          <span style={{ color: '#9CA3AF', fontSize: 12 }}>· {s.locationName}</span>
                        )}
                      </label>
                    ))}
                </div>
              )}
            </div>
          </Field>

          <Field label={t('composer.variant.label')}>
            <div>
              {(
                [
                  { heading: 'composer.variantGroup.style', help: null, items: ['celebration', 'manager', 'coaching', 'alert'] as Variant[] },
                  { heading: 'composer.variantGroup.informational', help: 'composer.variantGroup.informational.help', items: ['system', 'shift', 'progress'] as Variant[] },
                ] as Array<{ heading: string; help: string | null; items: Variant[] }>
              ).map((group, idx) => (
                <div key={group.heading} style={{ marginTop: idx === 0 ? 0 : 14 }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: 0.6,
                      textTransform: 'uppercase',
                      color: '#6B7280',
                      marginBottom: 6,
                    }}
                  >
                    {t(group.heading)}
                  </div>
                  {group.help && (
                    <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 8 }}>
                      {t(group.help)}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {group.items.map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setVariant(v)}
                        style={{
                          padding: '8px 14px',
                          border: variant === v ? '2px solid #3B82F6' : '1px solid #E5E7EB',
                          background: variant === v ? '#EFF6FF' : 'white',
                          borderRadius: 8,
                          cursor: 'pointer',
                          fontWeight: 600,
                          color: variant === v ? '#1E40AF' : '#374151',
                        }}
                      >
                        {t(`composer.variant.${v}`)}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Field>

          <Field label={t('composer.titleField')}>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={140}
              style={inputStyle}
              placeholder="Short, clear headline"
            />
          </Field>

          <Field label={t('composer.bodyField')}>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={500}
              rows={5}
              style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
              placeholder="Body of the message"
            />
          </Field>

          <Field label={t('composer.deepLinkField')}>
            <input
              value={deepLink}
              onChange={(e) => setDeepLink(e.target.value)}
              style={inputStyle}
              placeholder="e.g. bespoke://shifts or /restaurant/server-scorecard"
            />
          </Field>

          <Field label={t('composer.schedule.label')}>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <RadioOption checked={scheduleNow} onChange={() => setScheduleNow(true)} label={t('composer.schedule.now')} />
              <RadioOption checked={!scheduleNow} onChange={() => setScheduleNow(false)} label={t('composer.schedule.later')} />
              {!scheduleNow && (
                <input
                  type="datetime-local"
                  value={fireAt}
                  onChange={(e) => setFireAt(e.target.value)}
                  style={{ ...inputStyle, width: 'auto' }}
                />
              )}
            </div>
          </Field>

          {/* Audience summary */}
          {audiencePreview && (
            <div style={{ background: '#F9FAFB', padding: 12, borderRadius: 8, fontSize: 13, color: '#374151' }}>
              {t('composer.audienceSummary', { total: audiencePreview.total })}.{' '}
              {Object.keys(audiencePreview.by_locale).length > 0 && (
                <span style={{ color: '#6B7280' }}>
                  {t('composer.languageBreakdown', {
                    breakdown: Object.entries(audiencePreview.by_locale)
                      .map(([loc, n]) => `${loc} (${n})`)
                      .join(', '),
                  })}
                </span>
              )}
            </div>
          )}

          {resultMsg && (
            <div style={{
              padding: 12,
              borderRadius: 8,
              background: resultMsg.kind === 'ok' ? '#ECFDF5' : '#FEF2F2',
              color: resultMsg.kind === 'ok' ? '#065F46' : '#991B1B',
              fontSize: 14,
            }}>
              {resultMsg.msg}
            </div>
          )}

          <div style={{ display: 'flex', gap: 12 }}>
            <button
              type="button"
              disabled={sending || !isValid}
              onClick={handleSend}
              style={{
                padding: '10px 20px',
                background: !isValid ? '#D1D5DB' : '#3B82F6',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                fontWeight: 700,
                cursor: !isValid ? 'not-allowed' : 'pointer',
              }}
            >
              {sending ? '…' : t('composer.send')}
            </button>
            <button
              type="button"
              disabled={sending}
              onClick={handleSendTestToMe}
              style={{
                padding: '10px 20px',
                background: 'white',
                color: '#374151',
                border: '1px solid #E5E7EB',
                borderRadius: 8,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {t('composer.sendTest')}
            </button>
          </div>
        </div>

        {/* Right column — live preview */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', marginBottom: 8, letterSpacing: 0.5 }}>
            {t('composer.preview.label').toUpperCase()}
          </div>
          <NotificationCard notification={previewNotification} readOnly />
        </div>
      </div>
      </div>
    </div>
  );
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 8,
  border: '1px solid #E5E7EB',
  fontSize: 14,
  outline: 'none',
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>{label}</div>
    {children}
  </div>
);

const RadioOption: React.FC<{ checked: boolean; onChange: () => void; label: string }> = ({ checked, onChange, label }) => (
  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 14 }}>
    <input type="radio" checked={checked} onChange={onChange} />
    {label}
  </label>
);

export default AdminNotificationsComposerPage;
